import { IconMail, IconPhone } from '@tabler/icons-react';

import { AvatarGroupList } from '@/components/Avatar';
import CustomImage from '@/components/CustomImage';
import ProgressBar from '@/components/ProgressBar';
import { Switch } from '@/components/Switch';
import { LessonStageConfig, CommonStatusConfig } from '@/constants/constant';
import { ERoleAccess, ECommonStatus } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { IParentDetails } from '@/types';
import { getAssetUrl, formatLastActive } from '@/utils';

export interface IParentDetailsContentProps {
  parentDetails: IParentDetails | null;
  onChildClick: (childId: string) => void;
  handleBlockParent: () => void;
}

const ParentDetailsContent = ({
  parentDetails,
  onChildClick,
  handleBlockParent,
}: IParentDetailsContentProps) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();

  return (
    <div className="space-y-10 w-full bg-white">
      <div className="flex flex-col items-center gap-4">
        <AvatarGroupList
          list={[
            {
              id: parentDetails?.id || '',
              name: parentDetails?.name || '',
              image: getAssetUrl(parentDetails?.asset),
            },
          ]}
          size={100}
          avatarClassName="w-25! h-25! text-4xl!"
          className="rounded-full overflow-hidden"
        />

        <div className="text-2xl font-bold text-neutral-primary text-center w-full wrap-anywhere capitalize">
          {parentDetails?.name}
        </div>

        {parentDetails?.user_role === 'parent' && (
          <span className="px-2 py-0.5 rounded-full bg-indigo-light-50 text-indigo-secondary text-xs font-semibold">
            {formatText('parent.parent-tag')}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="parent-status"
          checked={parentDetails?.status === ECommonStatus.ACTIVE}
          onChange={handleBlockParent}
        />
        <span className="text-sm text-neutral-primary leading-5">
          {formatText('user-management.status')}:{' '}
          <span className="font-medium">
            {parentDetails?.status !== undefined &&
              formatText(
                CommonStatusConfig[
                  parentDetails.status as keyof typeof CommonStatusConfig
                ]?.label
              )}
          </span>
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="flex gap-2">
          <IconMail size={20} className="text-neutral-hover" />
          <div className="flex flex-col">
            <span className="text-sm text-neutral-secondary">
              {formatText('user-management.email')}
            </span>
            <span className="text-base text-neutral-primary max-w-58 overflow-wrap break-all">
              {parentDetails?.email ?? '-'}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <IconPhone size={20} className="text-neutral-hover" />
          <div className="flex flex-col">
            <span className="text-sm text-neutral-secondary">
              {formatText('user-management.phone-number')}
            </span>
            <span className="text-base text-neutral-primary">
              {parentDetails?.phone_code && parentDetails?.phone_number
                ? `${parentDetails.phone_code} ${parentDetails.phone_number}`
                : '-'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-neutral-primary">
            {formatText('parent.columns.linked-children')}
          </span>
          {(parentDetails?.linked_children_count ?? 0) > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-neutral-light-100 text-xs font-semibold">
              {(parentDetails?.linked_children_count ?? 0)
                ?.toString()
                ?.padStart(2, '0')}
            </span>
          )}
        </div>

        <div className="space-y-4">
          {parentDetails?.linked_children?.length === 0 && (
            <div className="flex items-center flex-col gap-3 justify-center w-full px-0 pt-10">
              <CustomImage
                src={'/assets/images/lesson-breakdown-empty.png'}
                alt="noData"
                width={100}
                height={100}
              />
              <p className="text-neutral-900 text-xl font-semibold sm:text-nowrap ">
                {formatText('common.no-data-found')}
              </p>
            </div>
          )}
          {parentDetails?.linked_children?.map((child) => {
            const levelConfig =
              LessonStageConfig[child?.stage as keyof typeof LessonStageConfig];
            if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

            return (
              <div
                key={child?.id}
                onClick={() => onChildClick(child?.id)}
                className="p-4 rounded-2xl border border-neutral-light-200 flex flex-col gap-4 cursor-pointer hover:bg-neutral-50"
              >
                <div className="flex justify-between items-start gap-2">
                  <span className="text-base font-semibold text-neutral-primary flex-1 wrap-anywhere capitalize">
                    {child?.name ?? '-'}
                  </span>
                  <span className="text-base text-neutral-secondary">
                    {child?.last_active_at
                      ? formatLastActive(Number(child?.last_active_at))
                      : '-'}
                  </span>
                </div>

                <span
                  className={`px-2 py-1 w-fit rounded-full text-xs font-semibold ${
                    levelConfig?.className ??
                    'bg-purple-light-50 text-purple-secondary'
                  }`}
                >
                  {levelConfig?.label
                    ? formatText(levelConfig?.label)
                    : child?.stage}
                </span>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-base text-neutral-secondary">
                      {formatText('user-management.progress')}
                    </span>
                    <span className="text-base font-semibold text-neutral-primary">
                      {child?.progress ?? 0}%
                    </span>
                  </div>

                  <ProgressBar
                    value={child?.progress ?? 0}
                    showPercentage={false}
                    barColor="bg-brand-default"
                    backgroundColor="bg-neutral-light-50"
                    height="h-2"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ParentDetailsContent;
